export const SECTION_NAME = "Transfer";

export const INTRO_TEXT = `Transfer transactions record the movement of
  inventory from one location to another.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "TR-12345",
    "date": "2019-05-08T09:13:00",
    "total": 0.0,
    "fromLocation": {
      "id": 1,
      "name": "Warehouse"
    },
    "toLocation": {
      "id": 3,
      "name": "Outdoor Storage"
    },
    "department": null,
    "shippingMethod": {
      "id": 7,
      "name": "UPS Ground"
    },
    "trackingNumber": "F84FSOI2O3FJV90012S",
    "shippingAmount": 0,
    "createBillForShippingAmount": true,
    "comment": null,
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Cust-Acct-No",
        "value": null,
        "dataType": "Number"
      }
    ],
    "archived": false,
    "summaryOnly": false,
    "hasSignature": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "class": null,
        "job": null,
        "workcenter": null,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "lot": null,
        "fromBin": {
          "id": 2,
          "name": "C18"
        },
        "toBin": {
          "id": 41,
          "name": "J30"
        },
        "description": null,
        "quantity": 1,
        "uom": {
          "id": 2,
          "name": "doz"
        },
        "serials": null
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests."
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a shipment, pass the string “auto”.`
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`
  },
  {
    name: "fromLocation",
    type: "reference",
    description: `Location from which inventory is being transferred.`
  },
  {
    name: "toLocation",
    type: "reference",
    description: `Location to which inventory is being transferred.`
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`
  },
  {
    name: "shippingMethod",
    type: "reference",
    description: `Shipping method for this transaction.`
  },
  {
    name: "trackingNumber",
    type: "string",
    description: `Carrier's tracking number.`
  },
  {
    name: "shippingAmount",
    type: "decimal",
    description: `Shipping amount field.`
  },
  {
    name: "createBillForShippingAmount",
    type: "boolean",
    description: `If true, the system will create a bill from the vendor associated with the shipping method. Valid only on create and update.`
  },
  {
    name: "comment",
    type: "string",
    description: `Transaction comment.`
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`
  },
  {
    name: "archived",
    type: "boolean",
    description: `Is the transaction archived?`,
    readOnly: true
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `Indicates if the summary parameter was set when retrieving back this record.`,
    readOnly: true
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true
  },
  {
    name: "lines",
    type: "array of line items",
    description: `The lines for this transfer.  See object structure below.`
  }
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this transfer item. ID field is ignored on create requests.`
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on this transaction.`
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true
  },
  {
    name: "lot",
    type: "reference",
    description: `Lot to which this item belongs.`
  },
  {
    name: "fromBin",
    type: "reference",
    description: `Bin from which inventory is being transferred.`
  },
  {
    name: "toBin",
    type: "reference",
    description: `Bin to which inventory is being transferred.`
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity of this line item.`
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`
  },
  {
    name: "serials",
    type: "array of references",
    description: `Serial number references for this line item.`
  }
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the shipment will be returned.`
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, id, comment, from location name, or to location name.`
  },
  {
    name: "archived",
    type: "string",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `
  },
  {
    name: "fromLocation",
    type: "string",
    description: `Filters transfer records according to the name of the originating location.`
  },
  {
    name: "toLocation",
    type: "string",
    description: `Filters transfer records according to the name of the destination location.`
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`
  }
];

export const SAMPLE_QUERY_REQUEST =
  "GET /api/v2/transfer?start=101&maxresults=100&status=open&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&fromLocation=warehouse&toLocation=Out%20for%20Rental&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
